import { getOnlyDate } from '~/astra'
import { deleteBatchPractitionerScheduleLocation, deleteBatchPractitionerScheduleStatus } from '~/store/di-entity.api'
import { StatusCode } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { PractitionerScheduleStatus, selectPractitionerScheduleLocations, selectPractitionerScheduleStatuses } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'

import { DatePractitionerCell, saveStatuses } from '../../../shared'

export function createGetStatusStateFn(selectedCells: DatePractitionerCell[], scheduleStatuses: Pick<PractitionerScheduleStatus, 'status_code'>[]) {
    return (status: StatusCode) => {
        const numCellsWithStatus = scheduleStatuses.filter(scheduleStatus => scheduleStatus.status_code === status).length
        return numCellsWithStatus === 0 ? 'none' : numCellsWithStatus === selectedCells.length ? 'all' : 'some'
    }
}

async function deleteStatusesByCode(scheduleStatuses: PractitionerScheduleStatus[], statusCode: string) {
    const statusesToDelete = scheduleStatuses.filter(scheduleStatus => scheduleStatus.status_code === statusCode)
    await deleteBatchPractitionerScheduleStatus(statusesToDelete.map(({ id }) => id))
    return statusesToDelete.length
}

export async function toggleStatus(selectedCells: DatePractitionerCell[], activity: StatusCode) {
    // Read statuses/locations imperatively at click time so callers don't subscribe
    // to the store — that way the StatusTag rendering it doesn't re-render on every mutation.
    const state = useStore.getState()
    const cellAttrs = selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId }))

    const scheduleStatuses = selectPractitionerScheduleStatuses(state, { where: { OR: cellAttrs } })
    const statusState = createGetStatusStateFn(selectedCells, scheduleStatuses)(activity)

    const hadUnavailable = scheduleStatuses.some(status => status.status_code === 'UNAVAILABLE')

    // allow overriding UNAVAILABLE status with any other status
    if (hadUnavailable && activity !== 'UNAVAILABLE') {
        await deleteStatusesByCode(scheduleStatuses, 'UNAVAILABLE')
    }

    if (statusState === 'none' || statusState === 'some') {
        // Adding special behaviour on UNAVAILABLE status.
        // This will delete all statuses and locations beforehand and only then apply the UNAVAILABLE status.
        if (activity === 'UNAVAILABLE') {
            const allLocations = selectPractitionerScheduleLocations(state, { where: { OR: cellAttrs } })
            await Promise.all([
                deleteBatchPractitionerScheduleStatus(scheduleStatuses.map(({ id }) => id)),
                deleteBatchPractitionerScheduleLocation(allLocations.map(({ id }) => id)),
            ])
        }

        const numberOfEdits = await saveStatuses(activity, selectedCells)
        void trackUserEvent('SURGEON_ACTIVITY_ADDED', { numberOfEdits, activity })
    }

    if (statusState === 'all') {
        const numberOfEdits = await deleteStatusesByCode(scheduleStatuses, activity)
        void trackUserEvent('SURGEON_ACTIVITY_REMOVED', { numberOfEdits, activity })
    }
}
